import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import { ClockIcon } from "@heroicons/react/24/outline";
import {
  CalendarIcon,
  OpenInNewWindowIcon,
  TriangleDownIcon,
} from "@radix-ui/react-icons";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetProjectStagesCompletion } from "src/api/Services/Projects/projects";
import {
  DueInternalUpdateData,
  InternalPhaseUpdateData,
  useCreateInternalUpdateForPhase,
  useGetPendingInternalUpdatesForUser,
  useGetPhaseInternalUpdates,
} from "src/api/Services/SharedSpace/phases";
import { TaskData, useGetPhaseTasks } from "src/api/Services/Tasks/tasks";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import TiptapEditor from "src/components/ui/editor/tiptap-editor";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { getInternalProjectUpdatesDueDateFilterKey } from "src/utils/filters";
import { getTimeDifference } from "src/utils/functions/dateTime";
import {
  InternalUpdate,
  TasksBreakdown,
} from "../Services/SharedSpace/Phases/Tabs/Overview";
import { getStatusSymbol } from "../Services/Tasks/Task";

type Props = {};

export default function MyProjectUpdates({}: Props) {
  const { data: pendingInternalUpdates } =
    useGetPendingInternalUpdatesForUser();
  const [dueDateFilter, setDueDateFilter] = useState<DueDate>(() => {
    const storedFilter = localStorage.getItem(
      getInternalProjectUpdatesDueDateFilterKey()
    );
    if (
      storedFilter &&
      ["overdue", "24h", "3d", "7d", "all"].includes(storedFilter)
    ) {
      return storedFilter as DueDate;
    } else {
      return "3d";
    }
  });

  useEffect(() => {
    localStorage.setItem(
      getInternalProjectUpdatesDueDateFilterKey(),
      dueDateFilter
    );
  }, [dueDateFilter]);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex select-none items-center space-x-2 text-sm">
          <div>My Project Updates</div>
        </div>
      </div>
      {/* <div className="flex h-14 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex select-none items-center">
          <Select
            value={dueDateFilter}
            onValueChange={(value) =>
              setDueDateFilter(value as "overdue" | "24h" | "3d" | "7d" | "all")
            }
          >
            <SelectTrigger className="h-8 py-0">
              <div className="mr-2.5 flex h-full items-center border-r border-input pr-2.5">
                Due Date
              </div>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="overdue">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    <div>Overdue</div>
                  </div>
                </SelectItem>
                <SelectItem value="24h">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <div>Next 24h</div>
                  </div>
                </SelectItem>
                <SelectItem value="3d">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <div>Next 3d</div>
                  </div>
                </SelectItem>
                <SelectItem value="7d">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                    <div>Next 7d</div>
                  </div>
                </SelectItem>
                <SelectItem value="all">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                    <div>All</div>
                  </div>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div> */}
      {pendingInternalUpdates ? (
        <Updates
          pendingInternalUpdates={pendingInternalUpdates}
          dueDateFilter={dueDateFilter}
          setDueDateFilter={setDueDateFilter}
        />
      ) : (
        <Loading className="mx-auto mt-[20vh]" />
      )}
    </div>
  );
}

type DueDate = "overdue" | "24h" | "3d" | "7d" | "all";
type UpdateState = "overdue" | "24h" | "upcoming";

function getUpdateState(update: DueInternalUpdateData): DueDate {
  const now = dayjs();
  const hours24 = dayjs().add(24, "hours");
  const days3 = dayjs().add(3, "days");
  const days7 = dayjs().add(7, "days");
  if (dayjs(update.dueDate).isBefore(now)) {
    return "overdue";
  } else if (dayjs(update.dueDate).isBefore(hours24)) {
    return "24h";
  } else if (dayjs(update.dueDate).isBefore(days3)) {
    return "3d";
  } else if (dayjs(update.dueDate).isBefore(days7)) {
    return "7d";
  } else {
    return "all";
  }
}

function Updates({
  pendingInternalUpdates,
  dueDateFilter,
  setDueDateFilter,
}: {
  pendingInternalUpdates: DueInternalUpdateData[];
  dueDateFilter: DueDate;
  setDueDateFilter: (filter: DueDate) => void;
}) {
  const sortedAndFilteredUpdates = useMemo(() => {
    return [...pendingInternalUpdates].sort((a, b) => {
      const aDueDate = dayjs(a.dueDate);
      const bDueDate = dayjs(b.dueDate);
      return aDueDate.diff(bDueDate);
    });

    // return {
    //   overdueUpdates: sortedUpdates.filter((update) => {
    //     const now = dayjs();
    //     return dayjs(update.dueDate).isBefore(now);
    //   }),
    //   upcomingUpdates: sortedUpdates.filter((update) => {
    //     const now = dayjs();
    //     return dayjs(update.dueDate).isAfter(now);
    //   }),
    // };

    // [...pendingInternalUpdates]
    // // .filter((update) => {
    // //   const now = dayjs();
    // //   const hours24 = dayjs().add(24, "hours");
    // //   const days3 = dayjs().add(3, "days");
    // //   const days7 = dayjs().add(7, "days");
    // //   if (dueDateFilter === "overdue") {
    // //     return dayjs(update.dueDate).isBefore(now);
    // //   } else if (dueDateFilter === "24h") {
    // //     return dayjs(update.dueDate).isBefore(hours24);
    // //   } else if (dueDateFilter === "3d") {
    // //     return dayjs(update.dueDate).isBefore(days3);
    // //   } else if (dueDateFilter === "7d") {
    // //     return dayjs(update.dueDate).isBefore(days7);
    // //   } else {
    // //     return true;
    // //   }
    // // })
    // .sort((a, b) => {
    //   const aDueDate = dayjs(a.dueDate);
    //   const bDueDate = dayjs(b.dueDate);
    //   return aDueDate.diff(bDueDate);
    // });
  }, [pendingInternalUpdates, dueDateFilter]);

  // const segmentedUpdates: {
  //   overdue: DueInternalUpdateData[]; // due date is before now
  //   dueToday: DueInternalUpdateData[]; // due date is after now and same day
  //   dueThisWeek: DueInternalUpdateData[]; // due date is after today and before 7 days from now
  //   dueAfterThisWeek: DueInternalUpdateData[]; // due date is after 7 days from now
  // } = useMemo(() => {
  //   if (!pendingInternalUpdates) {
  //     return {
  //       overdue: [],
  //       dueToday: [],
  //       dueThisWeek: [],
  //       dueAfterThisWeek: [],
  //     };
  //   }
  //   const now = dayjs();
  //   const endOfToday = dayjs().endOf("day");
  //   const endOfNext7Days = dayjs().add(7, "days");
  //   return {
  //     overdue: pendingInternalUpdates.filter((update) =>
  //       dayjs(update.dueDate).isBefore(now)
  //     ),
  //     dueToday: pendingInternalUpdates.filter((update) =>
  //       dayjs(update.dueDate).isBetween(now, endOfToday, null, "[]")
  //     ),
  //     dueThisWeek: pendingInternalUpdates.filter((update) =>
  //       dayjs(update.dueDate).isBetween(endOfToday, endOfNext7Days, null, "(]")
  //     ),
  //     dueAfterThisWeek: pendingInternalUpdates.filter((update) =>
  //       dayjs(update.dueDate).isAfter(endOfNext7Days)
  //     ),
  //   };
  // }, [pendingInternalUpdates]);

  const [activeProjectUpdate, setActiveProjectUpdate] =
    useState<DueInternalUpdateData | null>(
      // sortedAndFilteredUpdates.overdueUpdates.length > 0
      //   ? sortedAndFilteredUpdates.overdueUpdates[0]
      //   : sortedAndFilteredUpdates.upcomingUpdates.length > 0
      //   ? sortedAndFilteredUpdates.upcomingUpdates[0]
      //   : null
      pendingInternalUpdates.length > 0 ? pendingInternalUpdates[0] : null
    );

  // useEffect(() => {
  //   if (
  //     !sortedAndFilteredUpdates.overdueUpdates.find(
  //       (update) => update._id === activeProjectUpdate?._id
  //     )
  //   ) {
  //     setActiveProjectUpdate(sortedAndFilteredUpdates[0]);
  //   }
  // }, [dueDateFilter]);

  function onSubmit() {
    // set the activeProjectUpdate to the next update
    // let overdueUpdateIndex = sortedAndFilteredUpdates.overdueUpdates.findIndex(
    //   (update) => update._id === activeProjectUpdate?._id
    // );
    // let upcomingUpdateIndex =
    //   sortedAndFilteredUpdates.upcomingUpdates.findIndex(
    //     (update) => update._id === activeProjectUpdate?._id
    //   );
    // if (overdueUpdateIndex !== -1) {
    //   if (
    //     overdueUpdateIndex ===
    //     sortedAndFilteredUpdates.overdueUpdates.length - 1
    //   ) {
    //     if (sortedAndFilteredUpdates.upcomingUpdates.length > 0) {
    //       setActiveProjectUpdate(sortedAndFilteredUpdates.upcomingUpdates[0]);
    //     } else {
    //       if (sortedAndFilteredUpdates.overdueUpdates.length > 1) {
    //         setActiveProjectUpdate(
    //           sortedAndFilteredUpdates.overdueUpdates[overdueUpdateIndex - 1]
    //         );
    //       } else {
    //         setActiveProjectUpdate(null);
    //       }
    //     }
    //   } else {
    //     setActiveProjectUpdate(
    //       sortedAndFilteredUpdates.overdueUpdates[overdueUpdateIndex + 1]
    //     );
    //   }
    // } else if (upcomingUpdateIndex !== -1) {
    //   if (
    //     sortedAndFilteredUpdates.upcomingUpdates.length - 1 ===
    //     upcomingUpdateIndex
    //   ) {
    //     if (sortedAndFilteredUpdates.upcomingUpdates.length > 1) {
    //       setActiveProjectUpdate(
    //         sortedAndFilteredUpdates.upcomingUpdates[upcomingUpdateIndex - 1]
    //       );
    //     } else {
    //       if (sortedAndFilteredUpdates.overdueUpdates.length > 0) {
    //         setActiveProjectUpdate(
    //           sortedAndFilteredUpdates.overdueUpdates[
    //             sortedAndFilteredUpdates.overdueUpdates.length - 1
    //           ]
    //         );
    //       }
    //     }
    //   } else {
    //     setActiveProjectUpdate(
    //       sortedAndFilteredUpdates.upcomingUpdates[upcomingUpdateIndex + 1]
    //     );
    //   }
    // } else {
    //   setActiveProjectUpdate(null);
    // }
    if (activeProjectUpdate) {
      let index = pendingInternalUpdates.findIndex(
        (update) => update._id === activeProjectUpdate._id
      );
      if (index !== -1) {
        if (index === pendingInternalUpdates.length - 1) {
          if (pendingInternalUpdates.length > 1) {
            setActiveProjectUpdate(pendingInternalUpdates[index - 1]);
          } else {
            setActiveProjectUpdate(null);
          }
        } else {
          setActiveProjectUpdate(pendingInternalUpdates[index + 1]);
        }
      }
    }
  }

  const overdueUpdates = useMemo(() => {
    return pendingInternalUpdates.filter((update) =>
      dayjs(update.dueDate).isBefore(dayjs())
    );
  }, [pendingInternalUpdates]);

  const updatesInNext24Hours = useMemo(() => {
    return pendingInternalUpdates.filter((update) =>
      dayjs(update.dueDate).isBetween(dayjs(), dayjs().add(1, "day"))
    );
  }, [pendingInternalUpdates]);

  const upcomingUpdates = useMemo(() => {
    return pendingInternalUpdates.filter((update) =>
      dayjs(update.dueDate).isAfter(dayjs().add(1, "day"))
    );
  }, [pendingInternalUpdates]);

  return (
    <>
      {overdueUpdates.length > 0 ||
      updatesInNext24Hours.length > 0 ||
      upcomingUpdates.length > 0 ? (
        <div className="flex flex-1 overflow-auto">
          {/* <div className="h-full  overflow-auto "> */}
          {/* <div className="flex-1 overflow-y-auto pb-20">
              {sortedAndFilteredUpdates.overdueUpdates.map((update) => (
                <ProjectUpdate
                  key={update._id}
                  update={update}
                  state={getUpdateState(update)}
                  onClick={() => setActiveProjectUpdate(update)}
                  active={activeProjectUpdate?._id === update._id}
                />
              ))}
            </div> */}
          <div className="w-72 shrink-0 overflow-y-auto border-r border-gray-200/70 pb-20">
            {overdueUpdates.length > 0 && (
              <div className="relative">
                <div className="sticky top-0 left-0 right-0 z-50 flex h-9 items-center border-b border-gray-200/70 bg-red-50/80 px-8 text-xs font-medium text-red-600 backdrop-blur-sm">
                  Overdue
                </div>
                {overdueUpdates.map((update) => (
                  <ProjectUpdate
                    key={update._id}
                    update={update}
                    state="overdue"
                    onClick={() => setActiveProjectUpdate(update)}
                    active={activeProjectUpdate?._id === update._id}
                  />
                ))}
              </div>
            )}
            {updatesInNext24Hours.length > 0 && (
              <div className="relative">
                <div className="sticky top-0 left-0 right-0 z-50 flex h-9 items-center border-b border-gray-200/70 bg-amber-50/80 px-8 text-xs font-medium text-amber-600 backdrop-blur-sm">
                  Due In 24 Hours
                </div>
                {updatesInNext24Hours.map((update) => (
                  <ProjectUpdate
                    key={update._id}
                    update={update}
                    state="24h"
                    onClick={() => setActiveProjectUpdate(update)}
                    active={activeProjectUpdate?._id === update._id}
                  />
                ))}
              </div>
            )}
            {upcomingUpdates.length > 0 && (
              <div className="relative">
                <div className="sticky top-0 left-0 right-0 z-50 flex h-9 items-center border-b border-gray-200/70 bg-gray-50/80 px-8 text-xs font-medium text-gray-600 backdrop-blur-sm">
                  Upcoming
                </div>
                {upcomingUpdates.map((update) => (
                  <ProjectUpdate
                    key={update._id}
                    update={update}
                    state="upcoming"
                    onClick={() => setActiveProjectUpdate(update)}
                    active={activeProjectUpdate?._id === update._id}
                  />
                ))}
              </div>
            )}
            {/* {segmentedUpdates.dueThisWeek.length > 0 && (
                  <div className="relative">
                    <div className="sticky top-0 left-0 right-0 flex h-9 items-center border-b border-gray-200/70 bg-gray-100/70 px-8 text-xs font-medium text-gray-600 backdrop-blur-sm">
                      Due This Week
                    </div>
                    {segmentedUpdates.dueThisWeek.map((update) => (
                      <ProjectUpdate
                        key={update._id}
                        update={update}
                        state="dueThisWeek"
                        onClick={() => setActiveProjectUpdate(update)}
                        active={activeProjectUpdate?._id === update._id}
                      />
                    ))}
                  </div>
                )}
                {segmentedUpdates.dueAfterThisWeek.length > 0 && (
                  <div className="relative">
                    <div className="sticky top-0 left-0 right-0 flex h-9 items-center border-b border-gray-200/70 bg-green-100/70 px-8 text-xs font-medium text-green-600 backdrop-blur-sm">
                      <div>Due This Month</div>
                    </div>
                    {segmentedUpdates.dueAfterThisWeek.map((update) => (
                      <ProjectUpdate
                        key={update._id}
                        update={update}
                        state="dueAfterThisWeek"
                        onClick={() => setActiveProjectUpdate(update)}
                        active={activeProjectUpdate?._id === update._id}
                      />
                    ))}
                  </div>
                )} */}
            {/* </div> */}
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            {/* <div className="flex h-12 items-center border-b border-gray-200/70 px-8">
      <div className="text-sm font-medium text-gray-600">
        {activeProjectUpdate?.project.name}
      </div>
    </div> */}

            {activeProjectUpdate && (
              <ActiveProjectUpdate
                activeProjectUpdate={activeProjectUpdate}
                onSubmit={onSubmit}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="mt-[20vh] flex h-full justify-center">
          {getMessageForNoUpdates(
            dueDateFilter,
            pendingInternalUpdates.length > 0,
            () => {
              setDueDateFilter("all");
            }
          )}
        </div>
      )}
    </>
  );
}

function ActiveProjectUpdate({
  activeProjectUpdate,
  onSubmit,
}: {
  activeProjectUpdate: DueInternalUpdateData;
  onSubmit: () => void;
}) {
  const { data: internalUpdates } = useGetPhaseInternalUpdates(
    activeProjectUpdate.project._id
  );
  const { data: tasks } = useGetPhaseTasks(activeProjectUpdate.project._id);
  return (
    <>
      {internalUpdates && tasks ? (
        <>
          <div className=" bg-gray-100/60 px-8 pt-6 pb-2">
            <div className="mb-6 flex items-center justify-between space-x-2">
              <div className="inline-flex h-7 items-center space-x-1 rounded-md border bg-white px-2.5 pr-1 text-[13px]">
                <div>{activeProjectUpdate?.project.name}</div>
                <Button
                  size={"icon"}
                  className="h-5 w-5 rounded"
                  variant="ghost"
                  onClick={() => {
                    window.open(
                      `/projects/${activeProjectUpdate?.project._id}`
                    );
                  }}
                >
                  <OpenInNewWindowIcon className="h-3 w-3" />
                </Button>
              </div>
              <DueTimePill update={activeProjectUpdate} />
            </div>
            <NewUpdateEditor
              update={activeProjectUpdate}
              onSubmit={onSubmit}
              previousUpdates={internalUpdates}
            />
          </div>
          <ProjectDetails
            update={activeProjectUpdate}
            project={activeProjectUpdate?.project}
          />
        </>
      ) : (
        <Loading className="mx-auto mt-[20vh]" />
      )}
    </>
  );
}

function getMessageForNoUpdates(
  dueDateFilter: DueDate,
  updateExists: boolean,
  onClick: () => void
) {
  if (!updateExists) {
    return (
      <div className="flex h-fit flex-col items-center justify-center gap-3">
        <div className="text-[15px] text-gray-700">No updates due</div>
      </div>
    );
  }
  if (dueDateFilter === "overdue") {
    return (
      <div className="flex h-fit flex-col items-center justify-center gap-3">
        <div className="text-[15px] text-gray-700">No overdue updates</div>
        {updateExists && (
          <div>
            <Button variant={"secondary"} onClick={onClick}>
              View All Due Updates
            </Button>
          </div>
        )}
      </div>
    );
  } else if (dueDateFilter === "24h") {
    return (
      <div className="flex h-fit flex-col items-center justify-center gap-3">
        <div className="text-[15px] text-gray-700">
          No updates due in the next 24 hours
        </div>
        {updateExists && (
          <div>
            <Button variant={"secondary"} onClick={onClick}>
              View All Due Updates
            </Button>
          </div>
        )}
      </div>
    );
  } else if (dueDateFilter === "3d") {
    return (
      <div className="flex h-fit flex-col items-center justify-center gap-3">
        <div className="text-[15px] text-gray-700">
          No updates due in the next 3 days
        </div>
        {updateExists && (
          <div>
            <Button variant={"secondary"} onClick={onClick}>
              View All Due Updates
            </Button>
          </div>
        )}
      </div>
    );
  } else if (dueDateFilter === "7d") {
    return (
      <div className="flex h-fit flex-col items-center justify-center gap-2">
        <div className="text-[15px] text-gray-700">
          No updates due in the next 7 days
        </div>
        {updateExists && (
          <div>
            <Button variant={"secondary"} onClick={onClick}>
              View All Due Updates
            </Button>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="text-[15px] text-gray-700">No project updates due</div>
    );
  }
}

function DueTimePill({ update }: { update: DueInternalUpdateData }) {
  const state = getUpdateState(update);
  const { unit, absValue } = getTimeDifference(
    new Date(update.dueDate),
    new Date()
  );
  const isOverdue = dayjs(update.dueDate).isBefore(dayjs());
  return (
    <div className="rounded-md bg-white">
      <div
        className={clsx(
          "inline-flex h-7 items-center space-x-2 rounded-md border px-2 pr-2.5 text-[13px]",
          state === "overdue"
            ? "border-red-600/20 bg-red-50 text-red-600"
            : state === "24h"
            ? "border-amber-600/20 bg-amber-50 text-amber-600"
            : state === "3d"
            ? "border-amber-600/20 bg-amber-50 text-amber-600"
            : state === "7d"
            ? "border-gray-200 bg-white text-gray-600"
            : "border-gray-200 bg-white text-gray-600"
        )}
      >
        <ClockIcon className="h-4 w-4" />
        <div>
          {isOverdue
            ? `${absValue} ${unit} overdue`
            : `Due in ${absValue} ${unit}`}
        </div>
      </div>
    </div>
  );
}

function NewUpdateEditor({
  update,
  onSubmit,
  previousUpdates,
}: {
  update: DueInternalUpdateData;
  onSubmit: () => void;
  previousUpdates: InternalPhaseUpdateData[];
}) {
  console.log(previousUpdates);
  const lastUpdate = useMemo(() => {
    const manualUpdates = previousUpdates
      ?.filter((u) => u.isManual && u.state !== "pending")
      .sort((a, b) => dayjs(a.createdAt).diff(dayjs(b.createdAt)));
    if (manualUpdates.length === 0) return null;
    return manualUpdates[manualUpdates.length - 1];
  }, [previousUpdates]);

  const [status, setStatus] = useState(lastUpdate?.status || "On-Track");
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Write project update...",
        showOnlyWhenEditable: false,
      }),
    ],
    autofocus: true,
    content: "",
  });

  useEffect(() => {
    editor?.commands.setContent("");
    editor?.commands.focus();
  }, [update._id]);

  const createInternalUpdateMutation = useCreateInternalUpdateForPhase(
    update.project._id,
    true
  );

  useEffect(() => {
    editor?.commands.focus();
  }, [editor]);

  return (
    <div
      onClick={() => {
        editor?.commands.focus();
      }}
      className="mx-auto flex min-h-[180px] max-w-2xl cursor-text flex-col rounded-lg border border-gray-200 bg-white pt-4 shadow-sm"
    >
      <div className="px-4 pb-2">
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
          }}
        >
          <SelectTrigger className="h-7 w-fit pr-2 pl-2.5 text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {["On-Track", "At Risk", "Off-Track"].map((option) => (
                <SelectItem key={option} value={option} className="">
                  <div className="flex items-center">
                    <div
                      className={clsx(
                        `mr-2 h-2 w-2 rounded-full`,
                        option === "On-Track" && "bg-green-400",
                        option === "At Risk" && "bg-amber-400",
                        option === "Off-Track" && "bg-red-400"
                      )}
                    />
                    <div>{option}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 px-4 pb-3">
        <TiptapEditor editor={editor} hideToolbar />
        {/* <div className="min-h-[180px] overflow-auto  rounded-md border border-input p-3 px-4 text-sm shadow-sm">
        </div> */}
      </div>
      <div className="flex items-center justify-between space-x-1.5 border-t border-gray-200/70 px-4 py-3">
        <div className="flex items-center space-x-2">
          {lastUpdate && (
            <>
              <div className="text-[13px] font-light text-gray-500">
                Last update was{" "}
                <Badge
                  className="mx-0.5"
                  variant={
                    lastUpdate.status === "On-Track"
                      ? "green"
                      : lastUpdate.status === "At Risk"
                      ? "amber"
                      : "red"
                  }
                >
                  {lastUpdate.status}
                </Badge>{" "}
                {getTimeOfLastUpdate(lastUpdate)} ago
              </div>
              <div></div>
            </>
          )}
        </div>
        <Button
          size={"sm"}
          onClick={() => {
            createInternalUpdateMutation.mutate({
              description: editor?.getHTML() ?? "",
              status: status,
            });
            onSubmit();
          }}
          disabled={createInternalUpdateMutation.isLoading || editor?.isEmpty}
        >
          {"Submit"}
        </Button>
      </div>
    </div>
  );
}

function useOpenTask() {
  const navigate = useNavigate();
  const location = useLocation();
  const openTask = (taskId: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("taskId", taskId);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };
  return openTask;
}

function getTimeOfLastUpdate(update: InternalPhaseUpdateData) {
  const { unit, absValue } = getTimeDifference(
    new Date(update.createdAt),
    new Date()
  );
  return `${absValue} ${unit}`;
}

function ProjectDetails({
  update,
  project,
}: {
  update: DueInternalUpdateData;
  project: DueInternalUpdateData["project"];
}) {
  const openTask = useOpenTask();
  const { data: tasks } = useGetPhaseTasks(project._id);
  const { data: internalUpdates } = useGetPhaseInternalUpdates(project._id);
  // const { stages } = useGetJourneyStages(project.journey._id);
  const milestones = useMemo(() => {
    const milestones = tasks?.filter((t) => t.isMilestone);
    return milestones;
  }, [tasks]);

  const overdueTasks = useMemo(() => {
    const overdueTasks = tasks?.filter(
      (t) =>
        t.dueDate &&
        dayjs(t.dueDate).isBefore(dayjs()) &&
        t.status !== "Completed" &&
        t.status !== "Canceled"
    );
    return overdueTasks;
  }, [tasks]);

  const activeTasks = useMemo(() => {
    const activeTasks = tasks?.filter(
      (t) => t.dueDate && t.status !== "Completed" && t.status !== "Canceled"
    );
    return activeTasks;
  }, [tasks]);
  return (
    <div className="flex-1 bg-gray-100/60 px-8 py-8">
      {!!tasks ? (
        <div className="grid grid-cols-1 gap-2 2xl:grid-cols-2">
          <div className="rounded-md border-[0.5px] bg-white p-6 py-5 text-sm shadow-sm">
            <div className="font-medium">Overview</div>
            <div className="mt-3">
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Progress
                </div>

                <TasksBreakdown tasks={tasks} />
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Stages
                </div>
                <ProjectStages update={update} />
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Milestones
                </div>
                {milestones && <Milestones milestones={milestones} />}
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Creation Date
                </div>
                <div className="flex h-8 min-w-[264px] items-center space-x-1.5 rounded-md px-3 text-[13px]">
                  {dayjs(project.createdAt).format("DD MMM YYYY")}&nbsp;
                  <span className="text-[13px] font-light text-gray-500">
                    (
                    {Math.min(
                      1,
                      dayjs(dayjs()).diff(project.createdAt, "days")
                    )}{" "}
                    {Math.min(
                      1,
                      dayjs(dayjs()).diff(project.createdAt, "days")
                    ) > 1
                      ? "days"
                      : "day"}{" "}
                    ago)
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Start Date
                </div>
                <div className="flex h-8 min-w-[264px] items-center space-x-1.5 rounded-md px-3 text-[13px]">
                  {project.assignedStartDate
                    ? dayjs(project.assignedStartDate).format("DD MMM YYYY")
                    : "-"}
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  End Date
                </div>
                <div className="flex h-8 min-w-[264px] items-center space-x-1.5 rounded-md px-3 text-[13px]">
                  {project.assignedEndDate
                    ? dayjs(project.assignedEndDate).format("DD MMM YYYY")
                    : "-"}
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-40 text-[13px] font-light text-gray-500">
                  Forecasted End Date
                </div>
                <div className="flex h-8 min-w-[264px] items-center space-x-1.5 rounded-md px-3 text-[13px]">
                  {project.forecastedCompletionDate
                    ? dayjs(project.forecastedCompletionDate).format(
                        "DD MMM YYYY"
                      )
                    : "-"}
                </div>
              </div>
            </div>
          </div>
          <div className="relative col-span-1 flex max-h-[320px] flex-col overflow-hidden overflow-y-auto rounded-md border-[0.5px] bg-white pt-5 text-sm shadow-sm">
            <div className="shrink-0 px-6 pb-5 font-medium">
              Previous Updates
            </div>

            {internalUpdates?.filter(
              (update) => update.state !== "pending" && update.isManual
            ).length === 0 ? (
              <div className="mx-6 mb-5 flex h-full min-h-[80px] items-center justify-center rounded-md border border-dashed">
                <div className="text-gray-500">No previous updates</div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto border-t border-gray-200/70">
                  {internalUpdates
                    ?.filter(
                      (update) => update.state !== "pending" && update.isManual
                    )
                    .map((update) => (
                      <InternalUpdate
                        update={update}
                        key={update._id?.toString()}
                      />
                    ))}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-b from-transparent via-white/60 to-white" />
              </>
            )}
          </div>
          <div className="col-span-1 flex flex-col rounded-md border-[0.5px] bg-white p-6 py-5 text-sm shadow-sm">
            <div className="font-medium">Tasks Closed Since Last Update</div>
            {tasks && (
              <TasksClosedSinceLastUpdate tasks={tasks} project={project} />
            )}
          </div>
          <div className="col-span-1 flex flex-col rounded-md border-[0.5px] bg-white p-6 py-5 text-sm shadow-sm">
            <div className="font-medium">Currently Active Tasks</div>
            {!!activeTasks?.length ? (
              <div className="mt-5 -ml-1.5 -mr-0.5 space-y-0.5">
                {activeTasks?.map((task) => (
                  <div
                    className="group flex cursor-pointer items-center gap-2 truncate rounded-md py-0.5 pl-1.5 pr-0.5 hover:bg-gray-100/80"
                    key={task._id?.toString()}
                    onClick={() => {
                      if (task._id) {
                        openTask(task._id);
                      }
                    }}
                  >
                    <div>{getStatusSymbol(task.status, false, "small")}</div>
                    <div className="min-w-0 flex-1 truncate text-gray-600">
                      {task.title}
                    </div>
                    <div
                      className={clsx(
                        "w-28 shrink-0",
                        task.dueDate &&
                          dayjs(task.dueDate).isBefore(dayjs()) &&
                          "text-red-600"
                      )}
                    >
                      {task.dueDate ? (
                        <div className="inline-flex h-6 items-center gap-1 rounded-md border bg-white px-1.5 text-xs">
                          <CalendarIcon className="h-3 w-3" />
                          <div className="w-10">
                            {dayjs(task.dueDate).format("DD MMM")}
                          </div>
                          {task.dueDate &&
                            dayjs(task.dueDate).isBefore(dayjs()) && (
                              <div className="flex items-center">
                                <TriangleDownIcon className="h-3 w-3 shrink-0" />
                                {dayjs(dayjs()).diff(task.dueDate, "days")}d
                              </div>
                            )}
                        </div>
                      ) : (
                        <div className="text-xs">"No Due Date"</div>
                      )}
                    </div>
                    <div className="flex w-32 shrink-0 justify-start truncate text-xs">
                      <div className="flex h-6 max-w-[128px] items-center rounded-md border bg-gray-50 px-1.5">
                        <div className="min-w-0 truncate">
                          {task.tasker.userType === "fullUser"
                            ? task.tasker.name
                            : task.tasker.userType === "invitedUser"
                            ? task.tasker.email
                            : "Unassigned"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-5 flex min-h-[80px] flex-1 items-center justify-center rounded-md border border-dashed text-sm text-gray-500">
                No active tasks
              </div>
            )}
          </div>
        </div>
      ) : (
        <Loading className="mx-auto mt-20" />
      )}
    </div>
  );
}

export function Milestones({ milestones }: { milestones: TaskData[] }) {
  return (
    <HoverCard openDelay={100} closeDelay={0}>
      <HoverCardTrigger asChild>
        <div className="text-[13px]">
          <div className="flex h-8 min-w-[264px] items-center space-x-1.5 rounded-md px-3 hover:bg-gray-50">
            {milestones?.map((milestone) => (
              <div key={milestone._id?.toString()}>
                {getStatusSymbol(milestone.status, false, "small")}
              </div>
            ))}
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start">
        <div className="space-y-2">
          {milestones?.map((milestone) => (
            <div
              className="flex items-center justify-between space-x-4"
              key={milestone._id?.toString()}
            >
              <div className="shrink-0">
                {getStatusSymbol(milestone.status, false, "small")}
              </div>
              <div className="flex-1 text-sm">{milestone.title}</div>
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
function ProjectStages({ update }: { update: DueInternalUpdateData }) {
  const { data: stages } = useGetProjectStagesCompletion(update.project._id);
  return (
    <div className="text-[13px]">
      <HoverCard openDelay={100} closeDelay={0}>
        <HoverCardTrigger asChild>
          <div className="flex h-8 w-[264px] items-center space-x-1.5 rounded-md px-3 hover:bg-gray-50">
            {stages?.map((stage) => (
              <div
                key={"stage"}
                className={clsx(
                  "h-3 w-3 rounded-sm border",
                  stage.completionPercent === 100
                    ? "border-green-600 bg-green-400"
                    : stage.completionPercent > 0
                    ? "border-green-500 bg-green-100"
                    : "border-gray-400"
                )}
              />
            ))}
          </div>
        </HoverCardTrigger>
        <HoverCardContent side="right" align="start">
          <div className="space-y-2">
            {stages?.map((stage) => (
              <div
                className="flex items-center justify-between space-x-4 text-sm"
                key={stage._id?.toString()}
              >
                <div className="max-w-[240px] flex-1 truncate">
                  {stage.name}
                </div>
                <Badge
                  variant={
                    stage.completionPercent === 100
                      ? "darkGreen"
                      : stage.completionPercent === 0
                      ? "gray"
                      : "green"
                  }
                >
                  {stage.completionPercent === 100
                    ? "100%"
                    : stage.completionPercent.toFixed(0) + "%"}
                </Badge>
              </div>
            ))}
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}

function TasksClosedSinceLastUpdate({
  project,
  tasks,
}: {
  project: DueInternalUpdateData["project"];
  tasks: TaskData[];
}) {
  const dateOfLastUpdate = dayjs(project.updatedAt);
  const tasksCompleted = tasks.filter(
    (t) =>
      t.status === "Completed" &&
      dayjs(t.completionDate).isAfter(dateOfLastUpdate)
  );
  const tasksCanceled = tasks.filter(
    (t) =>
      t.status === "Canceled" &&
      dayjs(t.completionDate).isAfter(dateOfLastUpdate)
  );
  // const tasksStuck = tasks.filter(
  //   (t) =>
  //     t.status === "Stuck" && dayjs(t.completionDate).isAfter(dateOfLastUpdate)
  // );

  return (
    <>
      {tasksCompleted.length + tasksCanceled.length > 0 ? (
        <>
          <ul className="mt-5 -ml-1.5 -mr-0.5">
            {tasksCompleted.map((task) => (
              <TaskRow task={task} key={task._id?.toString()} />
            ))}
            {tasksCanceled.map((task) => (
              <TaskRow task={task} key={task._id?.toString()} />
            ))}
          </ul>
        </>
      ) : (
        <div className="mt-5 flex min-h-[80px] flex-1 items-center justify-center rounded-md border border-dashed text-sm text-gray-500">
          No tasks closed since last update
        </div>
      )}
    </>
    // <div className="mt-4 space-y-4">
    //   {tasksCompleted.length > 0 && (
    //     <div className="space-y-1 text-sm">
    //       <div className="flex items-center gap-1.5 text-gray-800">
    //         <Badge variant="green">Completed</Badge>
    //       </div>
    //     </div>
    //   )}
    //   {tasksStuck.length > 0 && (
    //     <div className="space-y-1 text-sm">
    //       <div className="flex items-center gap-1.5 text-gray-800">
    //         <div>{getStatusSymbol("Stuck", false, "small")}</div>
    //         <div>Tasks Stuck</div>
    //       </div>
    //       <ul className="space-y-0.5 pl-[20px]">
    //         {tasksStuck.map((task) => (
    //           <TaskRow task={task} key={task._id?.toString()} />
    //         ))}
    //       </ul>
    //     </div>
    //   )}
    //   {tasksCanceled.length > 0 && (
    //     <div className="space-y-1 text-sm">
    //       <div className="flex items-center gap-1.5 text-gray-800">
    //         <div>{getStatusSymbol("Canceled", false, "small")}</div>
    //         <div>Tasks Canceled</div>
    //       </div>
    //       <ul className="space-y-0.5 pl-[20px]">
    //         {tasksCanceled.map((task) => (
    //           <TaskRow task={task} key={task._id?.toString()} />
    //         ))}
    //       </ul>
    //     </div>
    //   )}
    // </div>
  );
}

function TaskRow({ task }: { task: TaskData }) {
  const openTask = useOpenTask();
  return (
    <li
      key={task._id?.toString()}
      className="group flex cursor-pointer items-center gap-2 truncate rounded-md py-0.5 pl-1.5 pr-0.5 hover:bg-gray-100/80"
      onClick={() => {
        if (task._id) {
          openTask(task._id);
        }
      }}
    >
      <div>{getStatusSymbol(task.status, false, "small")}</div>
      <div className="min-w-0 flex-1 truncate text-gray-600">{task.title}</div>
      {/* <div className="min-w-[20px] flex-1 border-t border-dashed border-transparent group-hover:border-gray-300" /> */}
      <div className="flex w-32 shrink-0 justify-start truncate text-xs">
        <div className="flex h-6 max-w-[128px] items-center rounded-md border bg-gray-50 px-1.5">
          <div className="min-w-0 truncate">
            {task.tasker.userType === "fullUser"
              ? task.tasker.name
              : task.tasker.userType === "invitedUser"
              ? task.tasker.email
              : "Unassigned"}
          </div>
        </div>
      </div>
    </li>
  );
}

function ProjectUpdate({
  update,
  state,
  onClick,
  active,
}: {
  update: DueInternalUpdateData;
  state: UpdateState;
  onClick: () => void;
  active: boolean;
}) {
  const { unit, absValue } = getTimeDifference(
    new Date(update.dueDate),
    new Date()
  );

  return (
    <div
      className={clsx(
        "relative flex group cursor-pointer items-center justify-between gap-4 border-b border-gray-200/40 pl-8 pr-6 pt-2 pb-2.5 hover:bg-gray-100/40",
        // active ? "" : "opacity-40"
      )}
      onClick={onClick}
    >
      <div
        className={clsx(
          "left-3.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full",
          "hidden group-hover:block group-hover:absolute",
          state === "overdue"
            ? "bg-red-300"
            : state === "24h"
            ? "bg-amber-300"
            : state === "upcoming"
            ? "bg-gray-300"
            : "bg-green-600"
        )}
      />
      <div
        className={clsx(
          "left-3.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full",
          active ? "absolute" : "hidden",
          state === "overdue"
            ? "bg-red-500"
            : state === "24h"
            ? "bg-amber-500"
            : state === "upcoming"
            ? "bg-gray-500"
            : "bg-green-600"
        )}
      />
      
      <div
        className={clsx(
          "min-w-0 truncate text-sm font-[350]",
          active ? "text-gray-900" : "text-gray-400"
        )}
      >
        {update.project.name}
      </div>

      {/* {(state === "7d" || state === "all") && (
        <div
          className={clsx(
            "inline-flex h-5 items-center rounded border border-gray-600/20 bg-gray-50 px-1 text-xs text-gray-600"
          )}
        >
          {`${absValue}${unit.charAt(0)}`}
        </div>
      )} */}
      {state === "upcoming" && (
        <div
          className={clsx(
            "inline-flex h-5 items-center rounded border border-gray-600/20 bg-gray-50 px-1 text-xs text-gray-600"
          )}
        >
          {`${absValue}${unit.charAt(0)}`}
        </div>
      )}
      {state === "24h" && (
        <div
          className={clsx(
            "inline-flex h-5 items-center rounded border border-amber-600/20 bg-amber-50 px-1 text-xs text-amber-600"
          )}
        >
          {`${absValue}${unit.charAt(0)}`}
        </div>
      )}
      {state === "overdue" && (
        <div
          className={clsx(
            "inline-flex h-5 items-center rounded border border-red-600/20 bg-red-50 px-1 text-xs text-red-600"
          )}
        >
          {`-${absValue}${unit.charAt(0)}`}
        </div>
      )}
    </div>
  );
}
